/**
 * Socket Event Aggregator
 * 
 * Centralized event bus for all socket events from Azure Web PubSub.
 * Components can register listeners and emit events in a type-safe way.
 * 
 * Usage:
 *   socketEventAggregator.on('auth:token_expired', handler);
 *   socketEventAggregator.off('auth:token_expired', handler);
 *   socketEventAggregator.emit('notification:received', data);
 */

export type EventHandler<T = any> = (data: T) => void | Promise<void>;

export interface EventPayload<T = any> {
  event: string;
  timestamp: string;
  data: T;
  userId?: string;
}

// Auth Events
export interface AuthTokenExpiredPayload {
  reason?: string;
  expires_at?: string;
}

export interface AuthInvalidTokenPayload {
  reason: string;
}

export interface AuthProfileAPIFailedPayload {
  error: string;
  action: string;
}

export interface AuthSessionInvalidatedPayload {
  reason: string;
  action: string;
}

export interface AuthUnauthorizedPayload {
  action: string;
  message: string;
}

export interface AuthTokenExpiringPayload {
  minutes_until_expiry: number;
  action: string;
}

// Notification Events
export interface NotificationReceivedPayload {
  id: string;
  user_id: string;
  notification_type: string;
  title: string;
  content?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

// Message Events
export interface MessageReceivedPayload {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  is_encrypted: boolean;
}

// Typing Events
export interface UserTypingPayload {
  chat_id: string;
  user_id: string;
}

export interface UserStopTypingPayload {
  chat_id: string;
  user_id: string;
}

// Chat Events
export interface UserJoinedPayload {
  chat_id: string;
  user_id: string;
}

export interface UserLeftPayload {
  chat_id: string;
  user_id: string;
}

// Collaboration Events
export interface DocumentEditPayload {
  document_id: string;
  user_id: string;
  edit_type: string;
  position: number;
  content_change?: string;
  version_number: number;
}

export interface UserStartedEditingPayload {
  document_id: string;
  user_id: string;
}

export interface UserStoppedEditingPayload {
  document_id: string;
  user_id: string;
}

export interface CursorMovedPayload {
  document_id: string;
  user_id: string;
  position: number;
}

/**
 * SocketEventAggregator
 * Centralized event bus for all socket events
 */
class SocketEventAggregator {
  private listeners: Map<string, Set<EventHandler>> = new Map();
  private eventHistory: Map<string, EventPayload> = new Map();
  private maxHistorySize = 100;

  /**
   * Register event listener
   * 
   * @param event Event name
   * @param handler Handler function
   * @returns Unsubscribe function
   */
  public on<T = any>(event: string, handler: EventHandler<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const handlers = this.listeners.get(event)!;
    handlers.add(handler as EventHandler);

    // Return unsubscribe function
    return () => {
      handlers.delete(handler as EventHandler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  /**
   * Register one-time event listener
   */
  public once<T = any>(event: string, handler: EventHandler<T>): () => void {
    const wrappedHandler = async (data: T) => {
      try {
        await handler(data);
      } finally {
        unsubscribe();
      }
    };

    const unsubscribe = this.on(event, wrappedHandler);
    return unsubscribe;
  }

  /**
   * Unregister event listener
   */
  public off<T = any>(event: string, handler: EventHandler<T>): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit event to all listeners
   */
  public async emit<T = any>(event: string, data: T): Promise<void> {
    const handlers = this.listeners.get(event);

    if (!handlers || handlers.size === 0) {
      return;
    }

    // Execute all handlers
    const promises = Array.from(handlers).map((handler) => {
      try {
        const result = handler(data);
        // If handler returns a promise, await it
        if (result instanceof Promise) {
          return result.catch((err) => {
            console.error(`Error in handler for ${event}:`, err);
          });
        }
      } catch (error) {
        console.error(`Error in handler for ${event}:`, error);
      }
      return Promise.resolve();
    });

    await Promise.all(promises);
  }

  /**
   * Emit event and track in history
   */
  public async emitWithHistory<T = any>(
    event: string,
    payload: EventPayload<T>
  ): Promise<void> {
    // Store in history
    this.eventHistory.set(event, payload);

    // Cleanup old history
    if (this.eventHistory.size > this.maxHistorySize) {
      const oldestKey = this.eventHistory.keys().next().value;
      this.eventHistory.delete(oldestKey || '');
    }

    // Emit to listeners
    await this.emit(event, payload.data);
  }

  /**
   * Get event history
   */
  public getHistory(event: string): EventPayload | undefined {
    return this.eventHistory.get(event);
  }

  /**
   * Clear event history
   */
  public clearHistory(): void {
    this.eventHistory.clear();
  }

  /**
   * Get all registered events
   */
  public getRegisteredEvents(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Get listener count for event
   */
  public getListenerCount(event: string): number {
    return this.listeners.get(event)?.size ?? 0;
  }

  /**
   * Remove all listeners
   */
  public removeAllListeners(event?: string): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

// Create singleton instance
export const socketEventAggregator = new SocketEventAggregator();

export default socketEventAggregator;
